import React, { memo, useCallback, useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  Stack,
  IconButton,
  CircularProgress
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import Editor from '../Editor';
import { addLineBreak } from '../addLineBreak';
import SpeakerEditor from './SpeakerEditor';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { FONT_FAMILY, BACKGROUND_COLORS } from './constants';
import { formatTimepoint } from './utils';
import { useColorPickerActions, useSpeakerColor } from '../../context/ColorPickerContext';
import { ServerURL } from '../ServerURL';

 const TranscriptionCard = ({
   itemKey,
   index,
   segment,
   speakers,
   cardState,
   isCurrentlyPlaying = false,
   meetingId,
   onTextEdit,
   onSpeakerEdit,
   onMouseEnter,
   onMouseLeave,
   onTextDoubleClick,
   onSpeakerDoubleClick,
   onEditClick,
   onAudioSeek,
   onSaveClick
 }) => {
  const { openColorPicker } = useColorPickerActions();
  
  // Local state for draft text while editing
  const [draftText, setDraftText] = useState(segment.text);
  const [isSaving, setIsSaving] = useState(false);
  
  // Ref to detect clicks outside the card
  const cardRef = useRef(null);
  
  // Calculate fallback color
  const colorID = speakers.indexOf(segment.speaker);
  const fallbackColors = [
    BACKGROUND_COLORS[colorID >= 0 ? colorID : 0] || BACKGROUND_COLORS[0]
  ];
  
  // Use the optimized hook for this specific speaker's color
  const backgroundColor = useSpeakerColor(segment.speaker, fallbackColors);

  // Handle click outside to save and exit edit mode
  const handleClickOutside = useCallback(async (event) => {
    if (cardRef.current && !cardRef.current.contains(event.target) && cardState.editableText && !isSaving) {
      // Save the draft text and exit edit mode when clicking outside
      setIsSaving(true);
      try {
        await onTextEdit(itemKey, draftText);
        if (onSaveClick) {
          onSaveClick(itemKey);
        }
      } catch (error) {
        console.error('Failed to save transcription on click outside:', error);
      } finally {
        setIsSaving(false);
      }
    }
  }, [cardState.editableText, isSaving, onTextEdit, itemKey, draftText, onSaveClick]);

  // Reset draft text when segment text changes (when not in edit mode)
  useEffect(() => {
    if (!cardState.editableText) {
      setDraftText(segment.text);
    }
  }, [segment.text, cardState.editableText]);

  // Add event listener for clicks outside when in edit mode
  useEffect(() => {
    if (cardState.editableText) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [cardState.editableText, handleClickOutside]);

   // Stable internal handlers bound to itemKey
   const handleTextEdit = useCallback((newText) => {
     // Only update draft text locally while editing - don't save to backend yet
     setDraftText(newText);
   }, []);
   
   const handleSave = useCallback(async () => {
     if (isSaving) return; // Prevent multiple save operations
     
     setIsSaving(true);
     try {
       // Save the draft text and exit edit mode
       await onTextEdit(itemKey, draftText);
       // Exit edit mode
       if (onSaveClick) {
         onSaveClick(itemKey);
       }
     } catch (error) {
       console.error('Failed to save transcription:', error);
       // Optionally show an error message to the user
     } finally {
       setIsSaving(false);
     }
   }, [itemKey, onTextEdit, draftText, onSaveClick, isSaving]);
   const handleSpeakerEdit = useCallback(async (newSpeaker) => {
     // Call the original handler first
     onSpeakerEdit(itemKey, newSpeaker);
     
     // If we have a meetingId and the speaker name changed, update the speaker color
     if (meetingId && newSpeaker && newSpeaker !== segment.speaker) {
       try {
         await fetch(
           `${ServerURL}/set_speaker_color/${meetingId}/${encodeURIComponent(newSpeaker)}/${encodeURIComponent(backgroundColor)}`,
           {
             method: 'GET',
             credentials: 'include'
           }
         );
       } catch (error) {
         console.warn('Failed to set speaker color for renamed speaker:', error);
       }
     }
   }, [itemKey, onSpeakerEdit, meetingId, segment.speaker, backgroundColor]);
   const handleMouseEnter = useCallback(() => onMouseEnter(itemKey), [itemKey, onMouseEnter]);
   const handleMouseLeave = useCallback(() => onMouseLeave(itemKey), [itemKey, onMouseLeave]);
   const handleTextDoubleClick = useCallback(() => onTextDoubleClick(itemKey), [itemKey, onTextDoubleClick]);
   const handleSpeakerDoubleClick = useCallback(() => onSpeakerDoubleClick(itemKey), [itemKey, onSpeakerDoubleClick]);
   const handleEditClick = useCallback(() => onEditClick(itemKey), [itemKey, onEditClick]);
   const handleAudioSeek = useCallback(() => onAudioSeek(itemKey), [itemKey, onAudioSeek]);
   const handleColorBandClick = useCallback(() => {
     // Use requestAnimationFrame to ensure modal opens on next frame for smoother UX
     requestAnimationFrame(() => {
       openColorPicker(segment.speaker, backgroundColor, meetingId);
     });
   }, [openColorPicker, segment.speaker, backgroundColor, meetingId]);


  // Text content - either editable or display
  const textContent = cardState.editableText ? (
    <div key={`${index}-text-edit`}>
       {Editor(
         draftText,
         handleTextEdit,
         false,
         true
       )}
    </div>
  ) : (
    <Box 
      component="h5" 
      onDoubleClick={handleTextDoubleClick}
      sx={{ margin: 0 }}
    >
      {addLineBreak(segment.text)}
    </Box>
  );


  return (
    <Box
      ref={cardRef}
      width="100%"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Card
        elevation={0}
        sx={{
          width: "100%",
          display: "flex",
          padding: 0,
          minWidth: 288,
          borderRadius: "12px",
          boxShadow: isCurrentlyPlaying
            ? "0 4px 12px 0 rgba(25, 118, 210, 0.3), 0 2px 4px 0 rgba(138, 148, 159, 0.2)"
            : "0 2px 4px 0 rgba(138, 148, 159, 0.2)",
          border: isCurrentlyPlaying ? "2px solid #1976d2" : "2px solid transparent",
          backgroundColor: "#ffffff",
          backgroundImage: isCurrentlyPlaying
            ? `linear-gradient(45deg, transparent 0%, rgba(25, 118, 210, 0.08) 100%)`
            : "none",
          transition: "all 0.3s ease",
          transform: isCurrentlyPlaying ? "scale(1.02)" : "scale(1)",
          overflow: "hidden"
        }}
      >
        {/* Colored Band */}
        <Box
          onClick={handleColorBandClick}
          sx={{
            width: "16px",
            background: `linear-gradient(180deg, ${backgroundColor} 0%, ${backgroundColor}CC 100%)`,
            minHeight: "100%",
            flexShrink: 0,
            borderTopLeftRadius: "10px",
            borderBottomLeftRadius: "10px",
            cursor: "pointer",
            transition: "all 0.2s ease",
            '&:hover': {
              transform: "scaleX(1.1)",
              boxShadow: "0 0 8px rgba(0, 0, 0, 0.2)"
            }
          }}
          title="Click to change speaker color"
        />
        
        {/* Main Content */}
        <Box width="100%" sx={{ padding: 2, position: "relative" }}>
          {/* Edit/Save Button - positioned at top right */}
          {cardState.editableText ? (
            <IconButton 
              aria-label={isSaving ? "saving transcription" : "save transcription"}
              onClick={handleSave}
              disabled={isSaving}
              sx={{
                position: "absolute",
                top: 4,
                right: 4,
                zIndex: 2,
                backgroundColor: isSaving ? "rgba(76, 175, 80, 0.6)" : "rgba(76, 175, 80, 0.9)",
                color: "white",
                '&:hover': {
                  backgroundColor: isSaving ? "rgba(76, 175, 80, 0.6)" : "rgba(76, 175, 80, 1)"
                },
                '&:disabled': {
                  color: "white",
                  backgroundColor: "rgba(76, 175, 80, 0.6)"
                },
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.2)"
              }}
              size="small"
            >
              {isSaving ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <SaveIcon fontSize="small" />
              )}
            </IconButton>
          ) : (cardState.hooverCard && (
            <IconButton 
              aria-label="edit transcription"
              onClick={handleEditClick}
              sx={{
                position: "absolute",
                top: 4,
                right: 4,
                zIndex: 2,
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                '&:hover': {
                  backgroundColor: "rgba(255, 255, 255, 1)"
                },
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)"
              }}
              size="small"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          ))}

          <Stack direction="column" width="100%" justifyContent="space-between">
            {/* Header: Speaker and Audio/Time */}
            <Stack direction="row" width="100%" justifyContent="space-between">
              {/* Speaker Section */}
              <Box
                sx={{
                  fontFamily: FONT_FAMILY,
                  fontSize: 14,
                  color: "grey.600",
                  letterSpacing: "1px",
                  marginBottom: "4px",
                  justifyContent: "flex-start"
                }}
                 onMouseEnter={handleMouseEnter}
                 onMouseLeave={handleMouseLeave}
                 onDoubleClick={handleSpeakerDoubleClick}
              >
                <SpeakerEditor
                  speaker={segment.speaker}
                  speakers={speakers}
                   onSpeakerChange={handleSpeakerEdit}
                  disabled={false}
                />
              </Box>

              {/* Timestamp with Play Icon */}
              <Stack direction="row" width="100%" justifyContent="flex-end" alignItems="center">
                <Stack 
                  direction="row" 
                  alignItems="center" 
                  spacing={0}
                  onClick={handleAudioSeek}
                  sx={{
                    cursor: "pointer",
                    padding: "4px 8px",
                    borderRadius: "4px",
                    transition: "all 0.2s ease",
                    display: "flex",
                    alignItems: "center",
                    '&:hover': {
                      backgroundColor: "rgba(25, 118, 210, 0.04)"
                    }
                  }}
                  title="Click to seek to this time"
                >
                  <PlayArrowIcon 
                    fontSize="small" 
                    sx={{ 
                      color: isCurrentlyPlaying ? "primary.main" : "grey.600",
                      '&:hover': {
                        color: "primary.main"
                      }
                    }} 
                  />
                  <Box
                    sx={{
                      top: "2px",
                      fontFamily: FONT_FAMILY,
                      fontSize: 14,
                      color: isCurrentlyPlaying ? "primary.main" : "grey.600",
                      letterSpacing: "1px",
                      fontWeight: isCurrentlyPlaying ? "bold" : "normal",
                      display: "flex",
                      alignItems: "center",
                      lineHeight: 1,
                      '&:hover': {
                        color: "primary.main"
                      }
                    }}
                  >
                    {formatTimepoint(segment.start)}
                  </Box>
                </Stack>
              </Stack>
            </Stack>

          </Stack>

          {/* Text Content */}
          {textContent}
        </Box>
      </Card>

    </Box>
  );
};

TranscriptionCard.displayName = 'TranscriptionCard';

 const areEqual = (prevProps, nextProps) => {
   return (
     prevProps.isCurrentlyPlaying === nextProps.isCurrentlyPlaying &&
     prevProps.segment === nextProps.segment &&
     prevProps.cardState === nextProps.cardState &&
     prevProps.speakers === nextProps.speakers &&
     prevProps.itemKey === nextProps.itemKey &&
     prevProps.index === nextProps.index &&
     prevProps.meetingId === nextProps.meetingId &&
     prevProps.onTextEdit === nextProps.onTextEdit &&
     prevProps.onSpeakerEdit === nextProps.onSpeakerEdit &&
     prevProps.onMouseEnter === nextProps.onMouseEnter &&
     prevProps.onMouseLeave === nextProps.onMouseLeave &&
     prevProps.onTextDoubleClick === nextProps.onTextDoubleClick &&
     prevProps.onSpeakerDoubleClick === nextProps.onSpeakerDoubleClick &&
     prevProps.onEditClick === nextProps.onEditClick &&
     prevProps.onAudioSeek === nextProps.onAudioSeek &&
     prevProps.onSaveClick === nextProps.onSaveClick
   );
 };

 export default memo(TranscriptionCard, areEqual); 